import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  Legend, ReferenceLine, ReferenceArea, ReferenceDot, ResponsiveContainer, Area, AreaChart, ComposedChart,
} from 'recharts';
import { api } from '../hooks/useApi';
import Icon from './Icon';
import './CourbeCuissonBatch.css';

// ─── CONSTANTES ───────────────────────────────

const ROLES = [
  { value: 'les_deux', label: 'Les deux (défaut)' },
  { value: 'interne',  label: 'Interne (viande)' },
  { value: 'ambiante', label: 'Ambiante (fumoir)' },
  { value: 'inactif',  label: 'Inactif (ignorer)' },
];

const TYPES_ANNOTATION = [
  { value: 'porte_ouverte',   label: 'Porte ouverte',    icone: 'Porte' },
  { value: 'ajout_bois',      label: 'Ajout de bois',    icone: 'Bois' },
  { value: 'probleme_fumoir', label: 'Probleme fumoir',  icone: 'Alerte' },
  { value: 'autre',           label: 'Autre',             icone: 'Note' },
];

const ICONES_TYPE = {
  porte_ouverte   : 'fermer',
  ajout_bois      : 'bois',
  probleme_fumoir : 'alerte',
  autre           : 'document',
};

const COULEURS_PROBES = ['#c8941a', '#16a34a', '#2563eb', '#c0392b', '#9333ea'];
const DEBOUNCE_REFRESH_MS = 10_000;

function formaterDuree(minutesTotal) {
  if (!minutesTotal && minutesTotal !== 0) return '--';
  const h = Math.floor(minutesTotal / 60);
  const m = minutesTotal % 60;
  return `${h}h${String(m).padStart(2, '0')}`;
}

function formaterTimestamp(ts) {
  if (!ts) return '--:--';
  try {
    return new Date(ts).toLocaleTimeString('fr-CA', { hour: '2-digit', minute: '2-digit' });
  } catch { return '--:--'; }
}

// ─── COMPOSANT PRINCIPAL ─────────────────────

/**
 * CourbeCuissonBatch — zone unifiée de suivi de température.
 * Props :
 *   batchId       {number}  — ID du batch courant
 *   produit       {string}  — "porc" | "brisket" | "cotes_levees" | "saucisse"
 *   sessionActive {boolean} — true si étape cuisson en cours (affiche sondes live + boutons)
 */
export default function CourbeCuissonBatch({ batchId, produit, sessionActive = false }) {
  // ── Sessions
  const [sessions, setSessions]           = useState([]);
  const [sessionId, setSessionId]         = useState(null);
  const [sessionStatut, setSessionStatut] = useState(null);

  // ── Données analyse
  const [analyse, setAnalyse]     = useState(null);
  const [courbeData, setCourbeData] = useState(null);

  // ── Sondes live
  const [sondesLive, setSondesLive]     = useState([]);
  const [rolesPre, setRolesPre]         = useState({}); // device_id → role (avant démarrage)

  // ── Seuils
  const [seuils, setSeuils]   = useState(null);

  // ── Upload CSV
  const [isDragging, setIsDragging] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadErreur, setUploadErreur]   = useState('');
  const fileInputRef     = useRef(null);
  const fileImportRef    = useRef(null);

  // ── Annotations
  const [annotations, setAnnotations]   = useState([]);
  const [modalAnnotation, setModalAnnotation] = useState(false);
  const [annotationType, setAnnotationType]   = useState('autre');
  const [annotationTexte, setAnnotationTexte] = useState('');
  const [savingAnnotation, setSavingAnnotation] = useState(false);

  // ── Refresh sondes
  const [refreshing, setRefreshing]   = useState(false);
  const [refreshMsg, setRefreshMsg]   = useState('');
  const lastRefreshRef                = useRef(0);

  // ── Actions session
  const [loadingSession, setLoadingSession] = useState(false);

  // ── Toast
  const [toast, setToast] = useState('');

  // ── Polling
  const pollingRef = useRef(null);

  // ─────────────────────────────────────────────
  // Chargement initial
  // ─────────────────────────────────────────────

  const chargerSessions = useCallback(async () => {
    if (!batchId) return;
    try {
      const toutes = await api.get(`/meater/sessions/batch/${batchId}`);
      const liste = Array.isArray(toutes) ? toutes : [];
      setSessions(liste);
      // Auto-sélectionner : en_cours d'abord, sinon la session terminée avec le plus de points
      // (évite de tomber sur une session vide artefact qui masquerait la vraie courbe)
      const enCours = liste.find(s => s.statut === 'en_cours');
      const terminee = liste
        .filter(s => s.statut === 'termine')
        .sort((a, b) => (b.nb_points || 0) - (a.nb_points || 0))[0];
      const defaut   = enCours || terminee || liste[0];
      if (defaut) {
        setSessionId(defaut.id);
        setSessionStatut(defaut.statut);
      } else {
        setSessionId(null);
        setSessionStatut(null);
      }
    } catch {
      setSessions([]);
    }
  }, [batchId]);

  const chargerSeuils = useCallback(async () => {
    try {
      const data = await api.get('/meater/config/seuils');
      setSeuils(data || {});
    } catch {
      setSeuils({});
    }
  }, []);

  const chargerSondesLive = useCallback(async () => {
    if (!sessionActive) return;
    try {
      const data = await api.get('/meater/devices/live');
      setSondesLive(Array.isArray(data) ? data : []);
    } catch {
      setSondesLive([]);
    }
  }, [sessionActive]);

  // Charge les rôles assignés aux sondes pour la session courante et
  // peuple rolesPre — permet au dropdown d'afficher la bonne valeur
  // quand on rouvre un batch avec une session déjà assignée.
  const chargerRoles = useCallback(async (sid) => {
    if (!sid) return;
    try {
      const rows = await api.get(`/meater/sessions/${sid}/sondes`);
      if (!Array.isArray(rows)) return;
      const map = {};
      for (const r of rows) map[r.device_id] = r.role;
      setRolesPre(map);
    } catch { /* silencieux */ }
  }, []);

  useEffect(() => {
    chargerSessions();
    chargerSeuils();
    chargerSondesLive();
  }, [chargerSessions, chargerSeuils, chargerSondesLive]);

  // ─────────────────────────────────────────────
  // Charger analyse + annotations quand sessionId change
  // ─────────────────────────────────────────────

  const chargerAnalyse = useCallback(async () => {
    if (!sessionId) { setAnalyse(null); setAnnotations([]); setCourbeData(null); setRolesPre({}); return; }
    try {
      const [ana, courbe] = await Promise.all([
        api.get(`/meater/sessions/${sessionId}/analyse`).catch(() => null),
        api.get(`/meater/sessions/${sessionId}/courbe`).catch(() => null),
      ]);
      setAnalyse(ana || null);
      setCourbeData(courbe || null);
      setAnnotations(ana?.annotations || []);
      chargerRoles(sessionId);
    } catch {
      setAnalyse(null);
    }
  }, [sessionId, chargerRoles]);

  useEffect(() => {
    chargerAnalyse();
  }, [chargerAnalyse]);

  // ─────────────────────────────────────────────
  // Polling 30s si session en_cours
  // ─────────────────────────────────────────────

  useEffect(() => {
    if (sessionStatut !== 'en_cours') {
      clearInterval(pollingRef.current);
      return;
    }

    async function poll() {
      await chargerAnalyse();
      await chargerSondesLive();
    }

    pollingRef.current = setInterval(poll, 30_000);
    return () => clearInterval(pollingRef.current);
  }, [sessionStatut, chargerAnalyse, chargerSondesLive]);

  // ─────────────────────────────────────────────
  // Refresh sondes (debounce 10s)
  // ─────────────────────────────────────────────

  async function rafraichirSondes() {
    const now = Date.now();
    if (now - lastRefreshRef.current < DEBOUNCE_REFRESH_MS) {
      setRefreshMsg('Attends quelques secondes...');
      setTimeout(() => setRefreshMsg(''), 3000);
      return;
    }
    lastRefreshRef.current = now;
    setRefreshing(true);
    setRefreshMsg('');
    try {
      await api.post('/meater/devices/refresh');
      const data = await api.get('/meater/devices/live');
      setSondesLive(Array.isArray(data) ? data : []);
    } catch (e) {
      if (e?.response?.status === 429) {
        setRefreshMsg('Attends 5s avant de rafraichir a nouveau.');
      } else {
        setRefreshMsg('Erreur lors du rafraichissement des sondes.');
      }
    } finally {
      setRefreshing(false);
    }
  }

  // ─────────────────────────────────────────────
  // Démarrer / Terminer session
  // ─────────────────────────────────────────────

  async function demarrerSession() {
    if (!batchId) return;
    setLoadingSession(true);
    try {
      const res = await api.post('/meater/sessions/demarrer', { batch_id: batchId, produit });
      const newId = res.session_id;
      // Assigner les rôles pré-sélectionnés
      for (const [device_id, role] of Object.entries(rolesPre)) {
        try {
          await api.post(`/meater/sessions/${newId}/sondes`, { device_id, role });
        } catch { /* non-bloquant */ }
      }
      setSessionId(newId);
      setSessionStatut('en_cours');
      await chargerSessions();
      afficherToast('Suivi MEATER démarré');
    } catch (e) {
      setRefreshMsg('Erreur : ' + (e?.response?.data?.error || e.message));
    } finally {
      setLoadingSession(false);
    }
  }

  async function terminerSession() {
    if (!sessionId) return;
    setLoadingSession(true);
    try {
      await api.post(`/meater/sessions/${sessionId}/terminer`);
      setSessionStatut('termine');
      await chargerAnalyse();
      afficherToast('Suivi terminé');
    } catch (e) {
      setRefreshMsg('Erreur : ' + (e?.response?.data?.error || e.message));
    } finally {
      setLoadingSession(false);
    }
  }

  // ─────────────────────────────────────────────
  // Porte ouverte (raccourci)
  // ─────────────────────────────────────────────

  async function signalerPorteOuverte() {
    if (!sessionId) return;
    try {
      await api.post(`/meater/sessions/${sessionId}/annotations/porte-ouverte`);
      afficherToast('Porte ouverte enregistrée');
    } catch (e) {
      setRefreshMsg('Erreur : ' + (e?.response?.data?.error || e.message));
    }
  }

  // ─────────────────────────────────────────────
  // Role probe
  // ─────────────────────────────────────────────

  async function changerRole(device_id, role) {
    // Met à jour l'état local immédiatement (UI réactive même si session active)
    setRolesPre(prev => ({ ...prev, [device_id]: role }));

    // Si une session est déjà en cours/existe, persister côté backend
    if (sessionId) {
      try {
        await api.post(`/meater/sessions/${sessionId}/sondes`, { device_id, role });
      } catch (err) {
        afficherToast('Erreur : rôle non enregistré');
        console.error('[MEATER] changerRole :', err?.response?.data || err?.message);
      }
    }
  }

  // ─────────────────────────────────────────────
  // Upload CSV
  // ─────────────────────────────────────────────

  async function handleFile(file) {
    if (!file) return;
    const ext = file.name.split('.').pop().toLowerCase();
    if (!['csv', 'json', 'xlsx', 'xls'].includes(ext)) {
      setUploadErreur('Format non supporté — utiliser CSV, JSON ou Excel');
      return;
    }
    setUploadLoading(true);
    setUploadErreur('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('batch_id', String(batchId));
      fd.append('produit', produit || 'import');
      const res = await api.postForm('/meater/sessions/upload-csv', fd);
      afficherToast(`${res.nb_points || 0} relevés importés`);
      await chargerSessions();
    } catch (e) {
      setUploadErreur(e?.response?.data?.error || 'Erreur lors du parsing du fichier');
    } finally {
      setUploadLoading(false);
    }
  }

  // ─────────────────────────────────────────────
  // Import JSON
  // ─────────────────────────────────────────────

  async function importerJson(e) {
    const fichier = e.target.files?.[0];
    e.target.value = '';
    if (!fichier) return;
    try {
      const texte = await fichier.text();
      const data  = JSON.parse(texte);
      if (!Array.isArray(data.points) || data.points.length === 0) {
        setUploadErreur('Fichier invalide : champ points[] requis.');
        return;
      }
      const payload = { ...data, batch_id: batchId, produit: data.produit || produit || 'import' };
      const res = await api.post('/meater/sessions/import', payload);
      afficherToast(`Importé (${res.nb_points_importes} points)`);
      await chargerSessions();
    } catch (err) {
      setUploadErreur('Import échoué : ' + (err?.response?.data?.error || err.message));
    }
  }

  // ─────────────────────────────────────────────
  // Annotations
  // ─────────────────────────────────────────────

  async function ajouterAnnotation() {
    if (!sessionId || !annotationTexte.trim()) return;
    setSavingAnnotation(true);
    try {
      await api.post(`/meater/sessions/${sessionId}/annotations`, {
        type  : annotationType,
        texte : annotationTexte.trim(),
      });
      setModalAnnotation(false);
      setAnnotationTexte('');
      setAnnotationType('autre');
      await chargerAnalyse();
      afficherToast('Annotation ajoutée');
    } catch (e) {
      setUploadErreur('Erreur : ' + (e?.response?.data?.error || e.message));
    } finally {
      setSavingAnnotation(false);
    }
  }

  async function supprimerAnnotation(annotationId) {
    if (!sessionId) return;
    if (!window.confirm('Supprimer cette annotation ?')) return;
    try {
      await api.delete(`/meater/sessions/${sessionId}/annotations/${annotationId}`);
      setAnnotations(prev => prev.filter(a => a.id !== annotationId));
    } catch { /* silencieux */ }
  }

  // ─────────────────────────────────────────────
  // Exports
  // ─────────────────────────────────────────────

  function exporter(format) {
    if (!sessionId) return;
    const token = localStorage.getItem('escamel_token');
    let url = `/api/meater/sessions/${sessionId}/export?format=${format}`;
    if (format === 'pdf') url = `/api/meater/sessions/${sessionId}/pdf`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const nomFichier = (r.headers.get('content-disposition') || '')
          .match(/filename="([^"]+)"/)?.[1] || `cuisson-${sessionId}.${format}`;
        return r.blob().then(blob => ({ blob, nomFichier }));
      })
      .then(({ blob, nomFichier }) => {
        const u = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = u; a.download = nomFichier;
        document.body.appendChild(a); a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(u);
      })
      .catch(e => setRefreshMsg('Export échoué : ' + e.message));
  }

  // ─────────────────────────────────────────────
  // Toast helper
  // ─────────────────────────────────────────────

  function afficherToast(msg) {
    setToast(msg);
    setTimeout(() => setToast(''), 3500);
  }

  // ─────────────────────────────────────────────
  // Seuils par produit
  // ─────────────────────────────────────────────

  function getSeuilSalubrite() {
    if (!seuils) return 160;
    const map = {
      porc        : seuils.temp_seuil_salubrite_porc_f,
      brisket     : seuils.temp_seuil_salubrite_brisket_f,
      cotes_levees: seuils.temp_seuil_salubrite_ribs_f,
      saucisse    : seuils.temp_seuil_salubrite_saucisse_f,
      saumon      : seuils.temp_seuil_salubrite_saumon_f,
    };
    return map[produit] || 160;
  }

  function getSeuilTendrete() {
    if (!seuils) return 190;
    const map = {
      porc        : seuils.temp_seuil_tendrete_porc_f,
      brisket     : seuils.temp_seuil_tendrete_brisket_f,
      cotes_levees: seuils.temp_seuil_tendrete_ribs_f,
      saucisse    : seuils.temp_seuil_tendrete_saucisse_f,
      saumon      : seuils.temp_seuil_tendrete_saumon_f,
    };
    return map[produit] || 190;
  }

  // ─────────────────────────────────────────────
  // Préparer données graphique
  // ─────────────────────────────────────────────

  const series = courbeData?.series || [];

  // Timestamp de référence (t0) = premier point de la première série.
  // Les zones grisées et annotations sont converties en minutes relatives
  // pour s'aligner sur l'axe X du graphique (dataKey='t_min').
  const t0Ms = series[0]?.points?.[0]?.time
    ? new Date(series[0].points[0].time).getTime()
    : null;

  function msVersMinRel(ms) {
    if (t0Ms == null || ms == null) return null;
    return (ms - t0Ms) / 60000;
  }

  // Annotations sur graphe (converties en minutes relatives)
  const annotationsGraphe = annotations
    .filter(a => a.timestamp)
    .map(a => ({
      t_min: msVersMinRel(new Date(a.timestamp).getTime()),
      type:  a.type,
      texte: a.texte,
    }))
    .filter(a => a.t_min != null && a.t_min >= 0);

  // Événements connexion pour zones grisées
  const evenementsConnexion = (analyse?.evenements_connexion || []).filter(ev =>
    ev.type === 'deconnexion' || ev.type === 'reconnexion'
  );

  // Zones de déconnexion : paires (deconnexion → reconnexion), en minutes relatives
  const zonesGrisees = [];
  let debutDecoRef = null;
  for (const ev of evenementsConnexion) {
    if (ev.type === 'deconnexion') {
      debutDecoRef = new Date(ev.timestamp).getTime();
    } else if (ev.type === 'reconnexion' && debutDecoRef) {
      const x1 = msVersMinRel(debutDecoRef);
      const x2 = msVersMinRel(new Date(ev.timestamp).getTime());
      if (x1 != null && x2 != null) zonesGrisees.push({ x1, x2 });
      debutDecoRef = null;
    }
  }

  // Stats depuis analyse
  const stats = analyse?.stats || null;

  // ─────────────────────────────────────────────
  // RENDU
  // ─────────────────────────────────────────────

  return (
    <div className="courbe-cuisson-batch">
      {/* Toast */}
      {toast && <div className="ccb-toast">{toast}</div>}

      {/* Modal annotation */}
      {modalAnnotation && (
        <div className="ccb-modal-overlay" onClick={() => setModalAnnotation(false)}>
          <div className="ccb-modal" onClick={e => e.stopPropagation()}>
            <h4>Ajouter une annotation</h4>
            <div className="ccb-modal-field">
              <label>Type</label>
              <select value={annotationType} onChange={e => setAnnotationType(e.target.value)}>
                {TYPES_ANNOTATION.map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
            <div className="ccb-modal-field">
              <label>Note (obligatoire)</label>
              <textarea
                value={annotationTexte}
                onChange={e => setAnnotationTexte(e.target.value)}
                placeholder="Décrire l'événement..."
              />
            </div>
            <div className="ccb-modal-actions">
              <button className="btn btn-ghost" onClick={() => setModalAnnotation(false)}>Annuler</button>
              <button
                className="btn btn-primary"
                onClick={ajouterAnnotation}
                disabled={savingAnnotation || !annotationTexte.trim()}
              >
                {savingAnnotation ? 'Enregistrement...' : 'Ajouter'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* En-tête */}
      <div className="ccb-header">
        <h3 className="ccb-titre">Suivi de cuisson</h3>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {sessionStatut === 'en_cours' && (
            <span className="ccb-live-badge">
              <span className="ccb-live-dot" />
              LIVE — toutes les 30s
            </span>
          )}
          {refreshMsg && <span className="muted" style={{ fontSize: 12 }}>{refreshMsg}</span>}
          {sessionActive && (
            <button
              className={`btn btn-ghost ccb-btn-refresh${refreshing ? ' ccb-refreshing' : ''}`}
              onClick={rafraichirSondes}
              disabled={refreshing}
            >
              <span className="ccb-refresh-icon"><Icon name="rafraichir" size={13} /></span> Rafraichir sondes
            </button>
          )}
        </div>
      </div>

      {/* ── Section sondes MEATER (si sessionActive) ── */}
      {sessionActive && (
        <div className="ccb-section-sondes card">
          <div className="ccb-sondes-titre">Sondes MEATER detectees</div>

          {sondesLive.length === 0 ? (
            <p className="muted" style={{ fontSize: 13 }}>
              Aucune sonde detectee. Lance le rafraichissement ou verifie ta configuration MEATER.
            </p>
          ) : (
            <div className="ccb-probes-grid">
              {sondesLive.map((s, i) => {
                const role = rolesPre[s.device_id] || 'les_deux';
                // Backend /devices/live retourne déjà les valeurs en °F (aliasées
                // depuis les colonnes temp_*_f). Aucune conversion à faire ici.
                const tempF = typeof s.temp_interne_f === 'number' ? s.temp_interne_f
                  : typeof s.temp_interne === 'number' ? s.temp_interne : null;
                const ambF  = typeof s.temp_ambiante_f === 'number' ? s.temp_ambiante_f
                  : typeof s.temp_ambiante === 'number' ? s.temp_ambiante : null;
                const estConnecte = s.connected !== false;
                return (
                  <div
                    key={s.device_id}
                    className={`ccb-probe-card role-${role}`}
                    style={{ opacity: estConnecte ? 1 : 0.5 }}
                  >
                    <div className="ccb-probe-nom">
                      {estConnecte ? '●' : '○'} {s.nom || s.device_id?.slice(-8) || 'Probe'}
                    </div>
                    <div className="ccb-probe-temps">
                      <div className="ccb-probe-temp-bloc">
                        <span className="ccb-probe-label">Interne</span>
                        <span className={`ccb-probe-valeur ${tempF !== null && tempF > getSeuilSalubrite() ? 'temp-ok' : 'temp-haute'}`}>
                          {tempF !== null ? `${tempF.toFixed(1)}°F` : '—'}
                        </span>
                      </div>
                      <div className="ccb-probe-temp-bloc">
                        <span className="ccb-probe-label">Fumoir</span>
                        <span className="ccb-probe-valeur">
                          {ambF !== null ? `${ambF.toFixed(1)}°F` : '—'}
                        </span>
                      </div>
                    </div>
                    {s.cook_state && (
                      <div className="muted" style={{ fontSize: 11 }}>{s.cook_state}</div>
                    )}
                    <select
                      className="ccb-probe-role-select"
                      value={role}
                      onChange={e => changerRole(s.device_id, e.target.value)}
                    >
                      {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                    </select>
                  </div>
                );
              })}
            </div>
          )}

          {/* Actions session */}
          <div className="ccb-session-actions">
            {sessionStatut !== 'en_cours' && (
              <button
                className="btn btn-primary"
                onClick={demarrerSession}
                disabled={loadingSession}
              >
                <Icon name="demarrer" size={14} />
                {loadingSession ? 'Demarrage...' : 'Demarrer le suivi MEATER'}
              </button>
            )}
            {sessionStatut === 'en_cours' && (
              <>
                <button
                  className="btn btn-danger"
                  onClick={terminerSession}
                  disabled={loadingSession}
                >
                  <Icon name="arreter" size={14} />
                  {loadingSession ? 'Arret...' : 'Terminer'}
                </button>
                <button
                  className="btn btn-ghost"
                  onClick={signalerPorteOuverte}
                >
                  <Icon name="fermer" size={13} /> J'ai ouvert la porte
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── Section import CSV / JSON ── */}
      <div className="ccb-section-upload card">
        <div className="ccb-upload-titre">Importer depuis thermometre externe</div>
        <div
          className={`ccb-upload-zone${isDragging ? ' dragging' : ''}`}
          onDragOver={e => { e.preventDefault(); setIsDragging(true); }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={e => { e.preventDefault(); setIsDragging(false); handleFile(e.dataTransfer.files[0]); }}
          onClick={() => fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.json,.xlsx,.xls"
            style={{ display: 'none' }}
            onChange={e => { handleFile(e.target.files[0]); e.target.value = ''; }}
          />
          {uploadLoading ? (
            <p className="muted">Analyse du fichier...</p>
          ) : (
            <>
              <div className="ccb-upload-icon"><Icon name="analytics" size={28} /></div>
              <p>Deposer CSV / JSON / Excel ici ou cliquer</p>
              <p className="muted" style={{ fontSize: 12 }}>max 5 Mo — ChefsTemp, ThermoWorks, etc.</p>
            </>
          )}
        </div>
        {uploadErreur && (
          <p className="danger" style={{ marginTop: 10, fontSize: 13 }}>{uploadErreur}</p>
        )}
      </div>

      {/* ── Stats ── */}
      {stats && (
        <div className="ccb-section-stats card">
          <div className="ccb-stats-titre">Statistiques de la cuisson</div>
          <div className="ccb-stats-grid">
            {stats.temp_max_interne_f != null && (
              <div className="ccb-stat-item">
                <span className="ccb-stat-label">Temp. max interne</span>
                <span className="ccb-stat-valeur gold">{stats.temp_max_interne_f.toFixed(1)}°F</span>
              </div>
            )}
            {stats.duree_totale_min != null && (
              <div className="ccb-stat-item">
                <span className="ccb-stat-label">Duree totale</span>
                <span className="ccb-stat-valeur">{formaterDuree(stats.duree_totale_min)}</span>
              </div>
            )}
            {stats.mins_above_salubrite != null && (
              <div className="ccb-stat-item">
                <span className="ccb-stat-label">Min. au-dessus de {getSeuilSalubrite()}°F (salubrite)</span>
                <span className={`ccb-stat-valeur ${stats.mins_above_salubrite >= 2 ? 'success' : 'danger'}`}>
                  {formaterDuree(stats.mins_above_salubrite)}
                </span>
              </div>
            )}
            {stats.mins_above_tendrete != null && (
              <div className="ccb-stat-item">
                <span className="ccb-stat-label">Min. au-dessus de {getSeuilTendrete()}°F (tendrete)</span>
                <span className={`ccb-stat-valeur ${stats.mins_above_tendrete >= 30 ? 'success' : 'warning'}`}>
                  {formaterDuree(stats.mins_above_tendrete)}
                </span>
              </div>
            )}
            {stats.temp_moyenne_fumoir_f != null && (
              <div className="ccb-stat-item">
                <span className="ccb-stat-label">Temp. moy. fumoir</span>
                <span className="ccb-stat-valeur">{stats.temp_moyenne_fumoir_f.toFixed(1)}°F</span>
              </div>
            )}
            {stats.temp_ecart_fumoir_f != null && (
              <div className="ccb-stat-item">
                <span className="ccb-stat-label">Stabilite fumoir (ecart-type)</span>
                <span className={`ccb-stat-valeur ${stats.temp_ecart_fumoir_f <= 10 ? 'success' : 'warning'}`}>
                  ±{stats.temp_ecart_fumoir_f.toFixed(1)}°F
                </span>
              </div>
            )}
          </div>

          {/* Anomalies */}
          {analyse?.anomalies && analyse.anomalies.length > 0 && (
            <div className="ccb-anomalies">
              {analyse.anomalies.map((a, i) => (
                <div key={i} className="ccb-anomalie-row">
                  <Icon name="alerte" size={13} />
                  <span>{a.description || a.type} {a.timestamp ? `a ${formaterTimestamp(a.timestamp)}` : ''}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Graphique ── */}
      {series.length > 0 && (
        <div className="ccb-section-graphe card">
          <div className="ccb-graphe-titre">Courbe de temperature (en minutes depuis le debut)</div>
          <div style={{ height: 320 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart margin={{ top: 10, right: 20, left: 0, bottom: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis
                  dataKey="t_min"
                  type="number"
                  domain={['dataMin', 'dataMax']}
                  tickFormatter={v => `${v}min`}
                  stroke="var(--muted)"
                  tick={{ fontSize: 11 }}
                  label={{ value: 'Temps (min)', position: 'insideBottom', offset: -10, fill: 'var(--muted)', fontSize: 11 }}
                  allowDuplicatedCategory={false}
                />
                <YAxis
                  stroke="var(--muted)"
                  tick={{ fontSize: 11 }}
                  label={{ value: '°F', angle: -90, position: 'insideLeft', fill: 'var(--muted)', fontSize: 11 }}
                  domain={[0, 'dataMax + 20']}
                />
                <Tooltip
                  contentStyle={{ background: 'var(--card)', border: '1px solid var(--border)', fontSize: 12 }}
                  labelFormatter={v => `${v} min`}
                  formatter={(val, name) => val != null ? [`${typeof val === 'number' ? val.toFixed(1) : val}°F`, name] : ['—', name]}
                />
                <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />

                {/* Ligne salubrité (rouge) */}
                <ReferenceLine
                  y={getSeuilSalubrite()}
                  stroke="var(--danger)"
                  strokeDasharray="6 3"
                  label={{ value: `${getSeuilSalubrite()}°F salubrite`, fill: 'var(--danger)', fontSize: 10, position: 'insideTopRight' }}
                />

                {/* Ligne tendreté (or) */}
                <ReferenceLine
                  y={getSeuilTendrete()}
                  stroke="var(--gold-500)"
                  strokeDasharray="6 3"
                  label={{ value: `${getSeuilTendrete()}°F tendrete`, fill: 'var(--gold-500)', fontSize: 10, position: 'insideTopRight' }}
                />

                {/* Zones grisées déconnexion */}
                {zonesGrisees.map((z, i) => (
                  <ReferenceArea key={i} x1={z.x1} x2={z.x2} fill="rgba(138,131,112,0.15)" />
                ))}

                {/* Annotations sur le graphe */}
                {annotationsGraphe.map((a, i) => (
                  <ReferenceDot
                    key={i}
                    x={a.t_min}
                    y={getSeuilSalubrite() + 10}
                    r={5}
                    fill="var(--gold-500)"
                    label={{ value: ICONES_TYPE[a.type] || '!', position: 'top', fontSize: 10 }}
                  />
                ))}

                {/* Courbes par sonde — points déjà en °F depuis lireCourbe() */}
                {series.map((s, i) => {
                  const pts = (s.points || []).map(p => ({
                    t_min         : p.t_min ?? Math.round((new Date(p.time).getTime() - new Date(series[0]?.points?.[0]?.time).getTime()) / 60000),
                    temp_interne_f : p.temp_interne_f ?? p.temp_interne ?? null,
                    temp_ambiante_f: p.temp_ambiante_f ?? p.temp_ambiante ?? null,
                  }));
                  const couleur = COULEURS_PROBES[i % COULEURS_PROBES.length];
                  return (
                    <React.Fragment key={s.device_id || i}>
                      {pts.some(p => p.temp_interne_f != null) && (
                        <Line
                          data={pts}
                          dataKey="temp_interne_f"
                          name={`${s.nom || s.device_id || `Sonde ${i+1}`} — interne`}
                          stroke={couleur}
                          strokeWidth={2.5}
                          dot={false}
                          connectNulls
                          type="monotone"
                        />
                      )}
                      {pts.some(p => p.temp_ambiante_f != null) && (
                        <Line
                          data={pts}
                          dataKey="temp_ambiante_f"
                          name={`${s.nom || s.device_id || `Sonde ${i+1}`} — fumoir`}
                          stroke={couleur}
                          strokeWidth={1.5}
                          strokeDasharray="5 5"
                          dot={false}
                          connectNulls
                          type="monotone"
                        />
                      )}
                    </React.Fragment>
                  );
                })}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* ── Annotations ── */}
      {sessionId && (
        <div className="ccb-section-annotations card">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <div className="ccb-annotations-titre" style={{ margin: 0 }}>Annotations</div>
            <button
              className="btn btn-ghost btn--sm"
              onClick={() => setModalAnnotation(true)}
            >
              <Icon name="ajouter" size={12} /> Ajouter une note
            </button>
          </div>

          {annotations.length === 0 ? (
            <p className="muted" style={{ fontSize: 13 }}>Aucune annotation pour l'instant.</p>
          ) : (
            <div className="ccb-annotations-list">
              {annotations.map(a => (
                <div key={a.id} className="ccb-annotation-row">
                  <span className="ccb-annotation-icone"><Icon name={ICONES_TYPE[a.type] || 'document'} size={13} /></span>
                  <span className="ccb-annotation-heure">{formaterTimestamp(a.timestamp)}</span>
                  <span className="ccb-annotation-texte">{a.texte}</span>
                  <span className="ccb-annotation-auteur">{a.operateur || ''}</span>
                  <button
                    className="btn btn-ghost btn--sm"
                    onClick={() => supprimerAnnotation(a.id)}
                  >
                    <Icon name="fermer" size={12} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Exports ── */}
      <div className="ccb-section-exports">
        {sessionId && (
          <>
            <button className="btn btn-ghost btn--sm" onClick={() => exporter('json')}>
              <Icon name="telecharger" size={13} /> JSON
            </button>
            <button className="btn btn-ghost btn--sm" onClick={() => exporter('csv')}>
              <Icon name="telecharger" size={13} /> CSV
            </button>
            <button className="btn btn-ghost btn--sm" onClick={() => exporter('pdf')}>
              <Icon name="document" size={13} /> PDF cuisson
            </button>
          </>
        )}
        <button
          className="btn btn-ghost btn--sm"
          onClick={() => fileImportRef.current?.click()}
        >
          <Icon name="televerser" size={13} /> Importer JSON
        </button>
        <input
          ref={fileImportRef}
          type="file"
          accept="application/json,.json"
          style={{ display: 'none' }}
          onChange={importerJson}
        />
        {/* Sélecteur de sessions si multiple */}
        {sessions.length > 1 && (
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 6, alignItems: 'center' }}>
            <span className="muted" style={{ fontSize: 12 }}>Session :</span>
            {sessions.map((s, i) => (
              <button
                key={s.id}
                className={`btn btn-ghost btn--sm${sessionId === s.id ? ' btn-primary' : ''}`}
                onClick={() => { setSessionId(s.id); setSessionStatut(s.statut); }}
              >
                {s.statut === 'en_cours' && <Icon name="demarrer" size={11} />}#{i + 1}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
